import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Button } from "@mui/material";
import { Formik, Form } from "formik";
import Swal from "sweetalert2";

import { validationSchema } from "./helpers/productForm.schema";
import { buildFormData } from "./helpers/productForm.utils";

import ProductBasicFields from "./form/ProductBasicFields";
import ProductImageField from "./form/ProductImageField";
import ProductCategoryFields from "./form/ProductCategoryFields";
import ProductVariationsSection from "./form/ProductVariationsSection";

import { AddProduct, UpdateProduct } from "../../../utils/product";
import { UpdateProductOption, AddProductOption, DeleteProductOption } from "../../../utils/productOptions";

export default function ProductFormDialog({
  open,
  onClose,
  initialValues,
  categories,
  onSaved, // callback שמחזיר savedProduct ל-AdminProducts כדי לעדכן slice
}) {
  const handleSubmit = async (values, { resetForm }) => {
    try {
      const fd = buildFormData(values);

      if (values.id !== 0) {
        // update product
        const savedProduct = await UpdateProduct(values.id, fd);
        onSaved?.({ mode: "edit", product: savedProduct });

        // sync variations
        if (values.variationType && Array.isArray(values.options)) {
          const updates = values.options
            .filter((o) => o?.id && o.id !== 0)
            .map((o) =>
              UpdateProductOption(o.id, {
                value: (o.value || "").trim(),
                extraPrice: Number(o.extraPrice) || 0,
                quantity: Number(o.quantity) || 0,
              })
            );

          const creates = values.options
            .filter((o) => !o?.id || o.id === 0)
            .filter((o) => (o.value || "").trim() !== "")
            .map((o) =>
              AddProductOption({
                productId: values.id,
                optionType: values.variationType,
                value: (o.value || "").trim(),
                extraPrice: Number(o.extraPrice) || 0,
                quantity: Number(o.quantity) || 0,
              })
            );

          await Promise.all([...updates, ...creates]);
        }

        resetForm();
        onClose();
        document.activeElement?.blur?.();
        setTimeout(() => {
          Swal.fire({ icon: "success", title: "המוצר עודכן", timer: 1500, showConfirmButton: false });
        }, 0);
      } else {
        // add product
        const savedProduct = await AddProduct(fd);
        onSaved?.({ mode: "add", product: savedProduct });

        resetForm();
        onClose();
        document.activeElement?.blur?.();
        setTimeout(() => {
          Swal.fire({ icon: "success", title: "המוצר נוסף", timer: 1500, showConfirmButton: false });
        }, 0);
      }
    } catch (error) {
      console.error("Failed to save product:", error);
      Swal.fire({ icon: "error", title: "שמירה נכשלה" });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      disableEnforceFocus
      disableRestoreFocus
      disableAutoFocus
    >
      <DialogTitle>{initialValues?.id ? "עריכת מוצר" : "הוסף מוצר"}</DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ dirty, isValid, errors, touched, setFieldValue, values, getFieldProps }) => (
          <Form encType="multipart/form-data">
            <DialogContent>
              <Grid container spacing={2}>
                <ProductBasicFields errors={errors} touched={touched} />
                <ProductImageField errors={errors} touched={touched} setFieldValue={setFieldValue} />
                <ProductCategoryFields
                  categories={categories}
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />
                <ProductVariationsSection
                  values={values}
                  setFieldValue={setFieldValue}
                  DeleteProductOption={DeleteProductOption}
                />
              </Grid>
            </DialogContent>

            <DialogActions>
              {getFieldProps("id").value !== 0 ? (
                <Button type="submit" variant="contained" color="primary">
                  שמור עריכת מוצר
                </Button>
              ) : (
                <Button type="submit" variant="contained" color="primary" disabled={!dirty || !isValid}>
                  הוסף מוצר
                </Button>
              )}

              <Button onClick={onClose} autoFocus>
                ביטול
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

